import type { FormMenuFolder, FormMenuNode, LabeledMenuItem } from './types';
import { parseFormMetadata } from './parseFormMetadata';

/**
 * Consolidates a flat list of menu items into label folders plus root-level
 * items for anything with no (or empty/invalid) Metadata.Labels.
 *
 * - Forms sharing the same label `name` (regardless of which file/source they
 *   came from) are merged into one folder.
 * - A label with no `displayOrder` of its own inherits the `sourceDisplayOrder`
 *   of the first item that carries it (falling back to sort-last only if
 *   that item has no order either).
 * - Folders are sorted by `displayOrder` ascending — lower numbers first,
 *   higher numbers last — then alphabetically by title as a tiebreaker.
 * - Items within a folder, and root-level items, preserve their input order
 *   (callers typically pre-sort the input list by their own displayOrder).
 * - A form carrying multiple labels is filed under each one.
 *
 * Pure function — no DOM, no React, no app-specific dependency. Safe to
 * relocate to a shared package independently of any rendering layer.
 */
export function buildFormMenuTree<T>(items: LabeledMenuItem<T>[]): FormMenuNode<T>[] {
  const folderMap = new Map<string, FormMenuFolder<T>>();
  const rootNodes: FormMenuNode<T>[] = [];

  for (const item of items) {
    const metadata = parseFormMetadata(item.metadataRaw);
    const labels = metadata?.Labels;

    if (!labels || labels.length === 0) {
      // No usable Metadata -> render at menu root level (per spec).
      rootNodes.push({ type: 'item', item });
      continue;
    }

    for (const label of labels) {
      let folder = folderMap.get(label.name);
      if (!folder) {
        folder = {
          type: 'folder',
          name: label.name,
          title: label.title,
          displayOrder: label.displayOrder ?? item.sourceDisplayOrder ?? Number.MAX_SAFE_INTEGER,
          isCollapsedByDefault: label.isCollapsedByDefault ?? false,
          items: [],
        };
        folderMap.set(label.name, folder);
      }
      folder.items.push(item);
    }
  }

  const folders = Array.from(folderMap.values()).sort(
    (a, b) => a.displayOrder - b.displayOrder || a.title.localeCompare(b.title)
  );

  return [...folders, ...rootNodes];
}
